/**
 * composite-connection.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Observable, Subject, Observer, Subscription } from "rxjs/Rx";
import { Message } from "../messages/general/";
import { Connection } from "./connection";

export const SYM_SKIP_SIPHON : symbol = Symbol("skip_siphon");

export class CompositeConnection<T> extends Connection<T> {
  /**
   * A composite connection is dead if any one of its sources is dead.
   */
  public get dead () : boolean
  {
    return this.sources.some((c : Connection<T>) => c.dead);
  }
  public set dead (deadness : boolean)
  {
    if (deadness)
    {
      this.close();
    }
  }

  public incoming : Observable<T>;

  public outgoingTarget : number = 0;

  constructor (
    private sources : Array<Connection<T>>,
    private completionTrigger : number = 1
  )
  {
    super();
    this.incoming = Observable.create((o : Observer<T>) => {
      const sub : Subscription = new Subscription();
      let completions : number = 0;
      sources.forEach((c : Connection<T>) => {
        sub.add(c.incoming.subscribe(
          (t : T) => {
            if (this.spigot(t))
            {
              o.next(t);
            }
          },
          (e : any) => o.error(e),
          () => {
            completions++;
            if (completions >= this.completionTrigger)
            {
              o.complete();
              sub.unsubscribe();
            }
          }
        ));
      });
    });
  }

  private spigot (item : T) : boolean
  {
    if (this.siphoning && !item[SYM_SKIP_SIPHON])
    {
      if (this.siphoned.indexOf(item) === -1)
      {
        this.siphoned.push(item);
      }
      return false;
    }
    return true;
  }
  public siphoning : boolean = false;
  public toggle () : void
  {
    this.siphoning = !this.siphoning;
  }
  public siphoned : T[] = [];

  public close () : void
  {
    this.sources.forEach((c : Connection<T>) => c.close());
  }

  public send (message : T) : void
  {
    if (this.sources.length === 0)
    {
      throw new Error("Cannot send; CompositeConnection has no targets!");
    }
    const target : Connection<T> = this.sources[this.outgoingTarget];
    target.send(message);
  }

  public setTarget (index : number) : void
  {
    if (Number.isInteger(index))
    {
      this.outgoingTarget = index % this.sources.length;
    }
    else
    {
      throw new Error("Can only set target index to an integer!");
    }
  }

  public nextTarget () : void
  {
    this.outgoingTarget = (this.outgoingTarget + 1) % this.sources.length;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/connection/composite-connection.ts